import { BriefPdf } from "./enums";

/** Shown for a brief PDF whose stored name is missing. */
export const BRIEF_PDF_FALLBACK_NAME = "mission-brief-document.pdf";

/**
 * Object key prefix every brief PDF is uploaded under.
 * Clean-up only ever deletes keys with this prefix, so a key that merely
 * appears in a brief can never point the delete at some other object.
 */
export const BRIEF_PDF_KEY_PREFIX = "brief-pdfs";

/**
 * App route that opens a file kept in private storage. The key follows as
 * the path, so a brief PDF link reads `/api/files/brief-pdfs/<uuid>`.
 */
export const PRIVATE_FILES_ROUTE = "/api/files";

const BRIEF_PDF_KEY_PATTERN = new RegExp(
  `^${BRIEF_PDF_KEY_PREFIX}/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$`,
  "i",
);

/** Whether an object key was issued for a brief PDF upload. */
export const isBriefPdfKey = (key: string): boolean =>
  BRIEF_PDF_KEY_PATTERN.test(key);

/**
 * The link that opens a brief PDF. The file is private, so the link goes
 * through the app's files route, which redirects to a short-lived signed URL.
 */
export const briefPdfHref = (pdf: BriefPdf): string =>
  `${PRIVATE_FILES_ROUTE}/${pdf.id}`;

export type BriefPdfColumns = {
  briefPdfs?: BriefPdf[] | null;
  pdfId?: string | null;
  pdfName?: string | null;
};

/**
 * The PDFs attached to a mission or deal memo brief. Reads the `briefPdfs`
 * list and falls back to the legacy single `pdfId`/`pdfName` pair for rows
 * written before the list existed, so every reader sees one shape.
 */
export const resolveBriefPdfs = ({
  briefPdfs,
  pdfId,
  pdfName,
}: BriefPdfColumns): BriefPdf[] => {
  if (briefPdfs) {
    return briefPdfs.map((pdf) => ({
      id: pdf.id,
      name: pdf.name || BRIEF_PDF_FALLBACK_NAME,
      ...(pdf.provider ? { provider: pdf.provider } : {}),
    }));
  }
  return pdfId ? [{ id: pdfId, name: pdfName || BRIEF_PDF_FALLBACK_NAME }] : [];
};

/**
 * The legacy single-PDF columns for a brief, kept in step with the first
 * entry of the list until every reader has moved over to `briefPdfs`.
 */
export const toLegacyBriefPdf = (pdfs: BriefPdf[]) => ({
  pdfId: pdfs[0]?.id ?? null,
  pdfName: pdfs[0]?.name ?? null,
});

/**
 * PDFs that were on a brief before an edit but are not after it, so the
 * caller can tidy up their files once nothing else references them.
 */
export const removedBriefPdfs = (
  before: BriefPdfColumns,
  after: BriefPdf[],
): BriefPdf[] =>
  resolveBriefPdfs(before).filter(
    (pdf) => !after.some((kept) => kept.id === pdf.id),
  );

/**
 * Keys in an edited brief that the server has no reason to trust: neither
 * issued for a brief PDF upload nor already attached to this brief. A save
 * carrying any of these is rejected, so the stored list only ever holds keys
 * the app handed out itself.
 */
export const unknownBriefPdfKeys = (
  before: BriefPdfColumns,
  after: BriefPdf[],
): string[] => {
  const attached = new Set(resolveBriefPdfs(before).map((pdf) => pdf.id));
  return after
    .map((pdf) => pdf.id)
    .filter((id) => !isBriefPdfKey(id) && !attached.has(id));
};
